import { Component, OnDestroy, AfterViewInit, ChangeDetectionStrategy, ElementRef, ViewChild } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { Subject, interval } from 'rxjs';
import { startWith, switchMap, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { VehicleLiveStatus } from '../../../../models/orderModel';

const POLL_INTERVAL_MS = 5000;
const DEFAULT_CENTER: [number, number] = [17.90287, 59.60517];

@Component({
  selector: 'app-gps-tracking',
  templateUrl: './gps-tracking.component.html',
  styleUrl: './gps-tracking.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class GpsTrackingComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapContainer') mapContainer: ElementRef<HTMLElement>;

  private map: mapboxgl.Map;
  private markers = new Map<number, mapboxgl.Marker>();
  private componentDestroyed$ = new Subject<boolean>();
  private mapReady = false;

  activeVehicleCount = 0;

  constructor(private backofficeService: BackOfficeService) {}

  ngAfterViewInit(): void {
    mapboxgl.accessToken = environment.mapbox.accessToken;
    this.map = new mapboxgl.Map({
      container: this.mapContainer.nativeElement,
      style: 'mapbox://styles/mapbox/streets-v11',
      zoom: 11,
      center: DEFAULT_CENTER
    });
    this.map.addControl(new mapboxgl.NavigationControl());

    this.map.on('load', () => {
      this.map.resize();
      this.mapReady = true;
      this.fitToMarkers();
    });

    interval(POLL_INTERVAL_MS)
      .pipe(
        startWith(0),
        switchMap(() => this.backofficeService.getVehicleTrackingLive()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe({
        next: vehicles => this.updateMarkers(vehicles || []),
        error: error => console.error('Error loading vehicle tracking status:', error)
      });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
    this.markers.forEach(marker => marker.remove());
    this.map?.remove();
  }

  private updateMarkers(vehicles: VehicleLiveStatus[]): void {
    this.activeVehicleCount = vehicles.length;

    if (!this.mapReady) return;

    const seenIds = new Set<number>();
    let markersChanged = false;

    for (const vehicle of vehicles) {
      seenIds.add(vehicle.id);
      const lngLat: [number, number] = [vehicle.longitude, vehicle.latitude];
      const existing = this.markers.get(vehicle.id);

      if (existing) {
        existing.setLngLat(lngLat);
        this.rotateMarker(existing, vehicle.heading || 0);
        this.updateMarkerLabel(existing, vehicle);
      } else {
        const el = this.createMarkerElement(vehicle);
        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat(lngLat)
          .addTo(this.map);
        this.markers.set(vehicle.id, marker);
        markersChanged = true;
      }
    }

    for (const [id, marker] of this.markers) {
      if (!seenIds.has(id)) {
        marker.remove();
        this.markers.delete(id);
        markersChanged = true;
      }
    }

    if (markersChanged || this.anyMarkerOutsideView()) {
      this.fitToMarkers();
    }
  }

  private anyMarkerOutsideView(): boolean {
    if (this.markers.size === 0) return false;

    const viewBounds = this.map.getBounds();
    for (const marker of this.markers.values()) {
      if (!viewBounds.contains(marker.getLngLat())) {
        return true;
      }
    }
    return false;
  }

  private createMarkerElement(vehicle: VehicleLiveStatus): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'vehicle-marker-wrapper';

    const icon = document.createElement('div');
    icon.className = 'vehicle-marker';
    icon.style.transform = `rotate(${vehicle.heading || 0}deg)`;
    wrapper.appendChild(icon);

    const label = document.createElement('div');
    label.className = 'vehicle-marker-label';
    label.textContent = this.labelText(vehicle);
    wrapper.appendChild(label);

    return wrapper;
  }

  private rotateMarker(marker: mapboxgl.Marker, heading: number): void {
    const icon = marker.getElement().querySelector<HTMLElement>('.vehicle-marker');
    if (icon) icon.style.transform = `rotate(${heading}deg)`;
  }

  private updateMarkerLabel(marker: mapboxgl.Marker, vehicle: VehicleLiveStatus): void {
    const label = marker.getElement().querySelector<HTMLElement>('.vehicle-marker-label');
    if (label) label.textContent = this.labelText(vehicle);
  }

  private labelText(vehicle: VehicleLiveStatus): string {
    const name = vehicle.name || `#${vehicle.id}`;
    const driver = vehicle.driverName ? ` · ${vehicle.driverName}` : '';
    return `${name}${driver}`;
  }

  private fitToMarkers(): void {
    if (this.markers.size === 0) return;

    const bounds = new mapboxgl.LngLatBounds();
    this.markers.forEach(marker => bounds.extend(marker.getLngLat()));

    this.map.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 500 });
  }
}
